/**
 * SEO utilities for dynamically updating meta tags
 * Helps Google and AI systems understand page content
 */

import { useEffect } from 'react';
import seoData from '../seo-routes.json';

type RouteMeta = { title: string; description: string; keywords?: string };

const SEO_BASE: string = (seoData as any).base || 'https://nightnavi.de';
const SEO_ROUTES: Record<string, RouteMeta> = (seoData as any).routes || {};
const SEO_PREFIX_ROUTES: Record<string, RouteMeta> = (seoData as any).prefixRoutes || {};

// Routes whose pages manage their own meta tags (dynamic content) — the central
// hook must not override them.
const SELF_MANAGED_PREFIXES = ['/event/', '/deal/', '/profile/'];
const SELF_MANAGED_EXACT = new Set(['/']);

/** Resolve the best-matching static meta entry for a clean pathname. */
export function resolveMeta(cleanPath: string): RouteMeta | null {
  if (SEO_ROUTES[cleanPath]) return SEO_ROUTES[cleanPath];
  for (const prefix of Object.keys(SEO_PREFIX_ROUTES)) {
    if (cleanPath.startsWith(prefix)) return SEO_PREFIX_ROUTES[prefix];
  }
  if (cleanPath === '/veranstalter') return SEO_ROUTES['/organizer-info'] || null;
  if (cleanPath === '/barmodell') return SEO_ROUTES['/bar-model'] || null;
  if (cleanPath === '/kampagnen') return SEO_ROUTES['/campaigns'] || null;
  return null;
}

/**
 * Central per-route SEO for static pages. Call once in App with the current
 * cleanPath. Skips routes whose own pages already set richer, dynamic meta.
 */
export function useSeo(cleanPath: string) {
  useEffect(() => {
    if (SELF_MANAGED_EXACT.has(cleanPath)) return;
    if (SELF_MANAGED_PREFIXES.some((p) => cleanPath.startsWith(p))) return;
    const meta = resolveMeta(cleanPath);
    if (!meta) return;
    const canonical = SEO_BASE + (cleanPath === '/' ? '/' : cleanPath);
    updateMetaTags({ title: meta.title, description: meta.description, url: canonical });
    if (meta.keywords) updateOrCreateMeta('name', 'keywords', meta.keywords);
  }, [cleanPath]);
}

interface MetaData {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'event' | 'product';
  author?: string;
}

export function updateMetaTags(meta: MetaData) {
  const { title, description, image, url, type = 'website' } = meta;

  // Update page title
  document.title = title;

  // Update or create meta tags
  updateOrCreateMeta('name', 'description', description);
  updateOrCreateMeta('property', 'og:title', title);
  updateOrCreateMeta('property', 'og:description', description);
  updateOrCreateMeta('property', 'og:type', type);
  updateOrCreateMeta('property', 'og:url', url || window.location.href);
  updateOrCreateMeta('name', 'twitter:title', title);
  updateOrCreateMeta('name', 'twitter:description', description);

  if (image) {
    updateOrCreateMeta('property', 'og:image', image);
    updateOrCreateMeta('name', 'twitter:image', image);
  }

  // Update canonical URL
  updateOrCreateCanonical(url || window.location.href);
}

function updateOrCreateMeta(attr: 'name' | 'property', attrValue: string, content: string) {
  let tag = document.querySelector(`meta[${attr}="${attrValue}"]`) as HTMLMetaElement;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, attrValue);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function updateOrCreateCanonical(url: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = url;
}

/**
 * Add structured data (Schema.org JSON-LD) for events
 */
export function addEventSchema(event: {
  name: string;
  description: string;
  date?: string;
  location?: string;
  image?: string;
  url?: string;
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    description: event.description,
    ...(event.date && { startDate: event.date }),
    ...(event.location && { location: { '@type': 'Place', name: event.location } }),
    ...(event.image && { image: event.image }),
    ...(event.url && { url: event.url }),
    organizer: {
      '@type': 'Organization',
      name: 'Night Navi',
      url: 'https://nightnavi.de'
    }
  };
  addJsonLd(schema);
}

/**
 * Add structured data for product/deal
 */
export function addProductSchema(product: {
  name: string;
  description: string;
  image?: string;
  price?: string;
  currency?: string;
  url?: string;
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    ...(product.image && { image: product.image }),
    ...(product.url && { url: product.url }),
    ...(product.price && {
      offers: {
        '@type': 'Offer',
        price: product.price,
        priceCurrency: product.currency || 'EUR',
        availability: 'https://schema.org/InStock'
      }
    })
  };
  addJsonLd(schema);
}

function addJsonLd(schema: any) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

/**
 * Clear existing JSON-LD schemas (useful when switching pages)
 */
export function clearJsonLd() {
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach((script, idx) => {
    // Keep the first two (Organization and WebSite)
    if (idx >= 2) {
      script.remove();
    }
  });
}
